import ExperimentProviderContext from '@carvana/experiment/ExperimentProviderContext';
import { MfeLoad } from '@carvana/microfrontend';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getMfeCacheBuster } from '@/common/MicroFrontEnds';
import {
  setSpinnerScriptInserted,
  setSpinnerType
} from '@/vdp/VehicleDetailsContainer/actions';
import GlobalContext from '~/context/vdp/GlobalContext';
import type { SpinnerType } from '~/contracts/vdp';
import { useClientSide } from '~/hooks/common';
import {
  getVehicleIdAndStockNumber,
  getVehicleInfoForSpinner
} from '~/selectors';
import { useSpinnerConfig } from './hooks';
import SpinnerErrorBoundary from './SpinnerErrorBoundary';

export default function SpinnerLoaderMfe() {
  const { experiments } = useContext(ExperimentProviderContext);
  const { spinnerReady } = useContext(GlobalContext);
  const dispatch = useDispatch();
  const { stockNumber, vehicleId } = useSelector(getVehicleIdAndStockNumber);
  const vehicleInfo = useSelector(getVehicleInfoForSpinner);
  const shouldLoadSpinner = useClientSide();
  const spinnerConfig = useSpinnerConfig();
  const spinnerLoadTimes = useRef<Record<string, number>>({});

  const { setLoading } = spinnerReady;

  const onLoadTimeEvent = useCallback(
    (eventName: string, readyForInteraction = false) => {
      spinnerLoadTimes.current[eventName] = Math.round(performance.now());
      if (readyForInteraction)
        Object.keys(spinnerLoadTimes.current).forEach(key => {
          datadogRum.addAction(key, { value: spinnerLoadTimes.current[key] });
        });
    },
    []
  );

  const onLoading = useCallback(() => {
    dispatch(setSpinnerScriptInserted());
  }, [dispatch]);

  const onSetSpinnerType = useCallback(
    (type: SpinnerType) => {
      dispatch(setSpinnerType(type));
    },
    [dispatch]
  );

  useEffect(() => {
    if (shouldLoadSpinner) setLoading();
  }, [setLoading, shouldLoadSpinner]);

  useEffect(() => {
    onLoadTimeEvent('vdpMfeSpinnerLoaderLoadStarted');
    MfeLoad({
      files: [
        [
          'SpinnerLoaderModule',
          [
            {
              component: 'default',
              default: {
                file: 'index.js',
                manifest: `${
                  process.env.CVNA_APP_CDN_URL
                }/spinner/spinner-loader/assets-manifest.json?v=${getMfeCacheBuster()}`
              }
            }
          ]
        ]
      ]
    }).then(() => {
      onLoadTimeEvent('vdpMfeSpinnerLoaderLoadCompleted');
    });
  }, [onLoadTimeEvent]);

  useEffect(() => {
    onLoadTimeEvent('vdpSpinnerLoaderComponentLoaded');
  }, [onLoadTimeEvent]);

  return shouldLoadSpinner ? (
    <MfeReactComponent
      module="SpinnerLoaderModule"
      component="default"
      onError={spinnerReady.setError}
      onLoading={onLoading}
      loadTimeEvent={onLoadTimeEvent}
      setSpinnerType={onSetSpinnerType}
      stockNumber={stockNumber}
      vehicleInfo={vehicleInfo}
      vehicleId={vehicleId}
      experiments={experiments}
      spinnerConfig={spinnerConfig}
      ErrorNode={SpinnerErrorBoundary}
    />
  ) : null;
}
